/**
 * Food-business category → array of illustrative fallback images.
 *
 * Every business_type maps to 3-6 verified images (custom illustrations
 * from /public/illustrations/food-categories/ + Pexels stock photos).
 * ``categoryIllustration(business_type, slug)`` returns a deterministic
 * pick from the array — same slug always shows the same image (stable
 * UX + no layout jitter), but the grid mixes them so the same photo
 * isn't repeated on adjacent cards.
 *
 * ``categoryHeroIllustration`` returns a large 1200x600 hero variant
 * (for category landing pages).
 *
 * Custom illustrations are indexed in
 *   /public/illustrations/food-categories/index.json
 * with tags so other sites can query & reuse them.
 */

// ---- Pexels stock pool (400x300-ish crop) --------------------------
const P = (id, w = 1000) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}`;

// ---- Local custom illustration URLs --------------------------------
// Card variant: 400x300 (WebP preferred; JPG fallback if WebP fails)
const L = (slug) => `/illustrations/food-categories/card/${slug}.webp`;
const LHero = (slug) => `/illustrations/food-categories/hero/${slug}.webp`;

// Category variant pools (custom illustrations listed first so the
// first business in each type shows the branded illustration).
const RESTAURANT   = [L("restaurant"), P(30951016), P(15756761), P(35243346), P(35452281), P(10895783)];
const SUSHI_ASIAN  = [P(32722834), P(10895783), P(35452281)];
const CAFE_COFFEE  = [L("cafe"), P(28793151), P(6612635), P(6829507), P(4669220), P(11236566), P(4920856)];
const BAKERY       = [L("bakery"), P(29570385), P(4916562), P(28793151)];
const GROCERY      = [L("grocery_store"), L("food_cart"), P(38079866), P(27592996)];
const SUPERMARKET  = [L("supermarket"), L("grocery_store"), P(38079866), P(27592996)];
const FARM         = [L("farm"), L("farmers_market"), L("local_producer"), P(27592996), P(2846217)];
const FARM_MARKET  = [L("farmers_market"), L("farm"), L("local_producer"), L("food_cart"), P(27592996), P(2846217)];
const SEAFOOD      = [L("specialty_store"), L("local_producer"), P(36697647)];
const BUTCHER      = [L("butcher"), L("local_producer"), L("specialty_store"), P(11686671), P(37663507), P(7883930)];
const FOOD_TRUCK   = [L("food_truck"), L("food_cart"), P(6586461)];
const FOOD_CART    = [L("food_cart"), L("food_truck"), P(6586461)];
const ICE_CREAM    = [L("specialty_store"), P(18003564), P(7761733)];
const BAR_ALCOHOL  = [L("brewery"), L("specialty_store"), P(35243346), P(35452281), P(15756761)];
const BREWERY      = [L("brewery"), L("specialty_store"), P(35243346), P(15756761)];
const FOOD_BANK    = [L("food_bank_charity"), P(4916562), P(27592996)];
const CHARITY      = [L("food_bank_charity"), P(4916562)];
const DISTRIBUTOR  = [L("food_distributor"), L("food_cart"), L("local_producer"), P(6586461)];
const CATERER      = [L("caterer"), L("food_festival"), L("bakery"), P(4916562)];
const CHOCOLATE    = [L("specialty_store"), L("local_producer"), L("bakery"), P(29570385)];
const CHEESE       = [L("specialty_store"), L("local_producer"), L("bakery"), P(4916562)];
const SPICE_TEA    = [L("specialty_store"), L("local_producer"), L("farmers_market")];
const HEALTH_FOOD  = [L("specialty_store"), L("farmers_market"), L("local_producer")];
const PIZZA_PLACE  = [L("pizza_pasta"), L("restaurant"), P(30951016), P(15756761)];
const BURGER_PLACE = [L("burger_place"), L("restaurant"), P(35243346), P(10895783)];

// Hero variants (1200x600) — used on category landing pages.
const HERO = {
  restaurant:        LHero("restaurant"),
  fast_food:         LHero("restaurant"),
  food_court:        LHero("restaurant"),
  pizza:             LHero("pizza_pasta"),
  pizzeria:          LHero("pizza_pasta"),
  burger:            LHero("burger_place"),
  burgers:           LHero("burger_place"),
  cafe:              LHero("cafe"),
  coffee_shop:       LHero("cafe"),
  tea_shop:          LHero("cafe"),
  bakery:            LHero("bakery"),
  pastry:            LHero("bakery"),
  chocolate_shop:    LHero("specialty_store"),
  confectionery:     LHero("specialty_store"),
  cheese_shop:       LHero("specialty_store"),
  deli:              LHero("bakery"),
  farmers_market:    LHero("farmers_market"),
  farm:              LHero("farm"),
  csa:               LHero("farm"),
  health_food:       LHero("specialty_store"),
  spice_shop:        LHero("specialty_store"),
  wine_shop:         LHero("brewery"),
  alcohol_shop:      LHero("brewery"),
  ice_cream:         LHero("specialty_store"),
  butcher:           LHero("butcher"),
  seafood_shop:      LHero("specialty_store"),
  food_truck:        LHero("food_truck"),
  food_cart:         LHero("food_cart"),
  food_distributor:  LHero("food_distributor"),
  food_warehouse:    LHero("food_distributor"),
  grocery:           LHero("grocery_store"),
  supermarket:       LHero("supermarket"),
  convenience_store: LHero("grocery_store"),
  beverages_shop:    LHero("grocery_store"),
  caterer:           LHero("caterer"),
  brewery:           LHero("brewery"),
  bar:               LHero("brewery"),
  pub:               LHero("brewery"),
  winery:            LHero("brewery"),
  distillery:        LHero("brewery"),
  food_bank:         LHero("food_bank_charity"),
  food_pantry:       LHero("food_bank_charity"),
  food_charity:      LHero("food_bank_charity"),
  soup_kitchen:      LHero("food_bank_charity"),
};

export const CATEGORY_ILLUSTRATIONS = {
  restaurant:        RESTAURANT,
  fast_food:         RESTAURANT,
  food_court:        RESTAURANT,
  pizza:             PIZZA_PLACE,
  pizzeria:          PIZZA_PLACE,
  burger:            BURGER_PLACE,
  burgers:           BURGER_PLACE,
  cafe:              CAFE_COFFEE,
  coffee_shop:       CAFE_COFFEE,
  bar:               BAR_ALCOHOL,
  pub:               BAR_ALCOHOL,
  ice_cream:         ICE_CREAM,
  bakery:            BAKERY,
  pastry:            BAKERY,
  chocolate_shop:    CHOCOLATE,
  confectionery:     BAKERY,
  cheese_shop:       CHEESE,
  supermarket:       SUPERMARKET,
  grocery:           GROCERY,
  convenience_store: GROCERY,
  health_food:       HEALTH_FOOD,
  deli:              BAKERY,
  food_truck:        FOOD_TRUCK,
  food_cart:         FOOD_CART,
  farmers_market:    FARM_MARKET,
  farm:              FARM,
  csa:               FARM,
  seafood_shop:      SEAFOOD,
  butcher:           BUTCHER,
  spice_shop:        SPICE_TEA,
  tea_shop:          SPICE_TEA,
  beverages_shop:    GROCERY,
  wine_shop:         BAR_ALCOHOL,
  alcohol_shop:      BAR_ALCOHOL,
  brewery:           BREWERY,
  winery:            BREWERY,
  distillery:        BREWERY,
  food_bank:         FOOD_BANK,
  food_pantry:       FOOD_BANK,
  food_charity:      CHARITY,
  soup_kitchen:      CHARITY,
  food_distributor:  DISTRIBUTOR,
  food_warehouse:    DISTRIBUTOR,
  caterer:           CATERER,
  other:             CAFE_COFFEE,
};

/** Deterministic string hash — same input always yields same index.
 *  DJB2 xor variant, small and hoist-free.
 */
function hash(str) {
  let h = 5381;
  for (let i = 0; i < (str || "").length; i++) {
    h = ((h << 5) + h) ^ str.charCodeAt(i);
  }
  return Math.abs(h);
}

/**
 * Get a category illustration URL. The ``key`` argument (business
 * slug, article slug, etc.) picks a deterministic variant from the
 * pool, so the same entity always shows the same image but the grid
 * mixes them.
 *
 * ``hints`` (optional) lets callers pass extra business signals so
 * Pizza / Burger places get their dedicated illustration even when
 * they're stored as generic business_type=restaurant|fast_food.
 * Shape: { subType?: string, cuisines?: string[], name?: string }
 */
export function categoryIllustration(businessType, key = "", hints = null) {
  // Cuisine / sub_type / name based override for Pizza & Burger
  if (hints) {
    const hay = [
      hints.subType || "",
      ...(Array.isArray(hints.cuisines) ? hints.cuisines : []),
      hints.name || "",
    ].join(" ").toLowerCase();
    if (/\b(pizza|pizzeria|pasta)\b/.test(hay)) {
      return PIZZA_PLACE[hash(key || "pizza") % PIZZA_PLACE.length];
    }
    if (/\b(burger|burgers|smashburger|cheeseburger)\b/.test(hay)) {
      return BURGER_PLACE[hash(key || "burger") % BURGER_PLACE.length];
    }
  }
  const pool = CATEGORY_ILLUSTRATIONS[businessType] || CATEGORY_ILLUSTRATIONS.other;
  if (!pool || pool.length === 0) return "";
  if (pool.length === 1) return pool[0];
  return pool[hash(key || businessType) % pool.length];
}

/**
 * Get the large 1200x600 hero variant for a category landing page.
 * Returns null if no custom hero exists — caller can fall back to
 * ``categoryIllustration`` or a Pexels image.
 */
export function categoryHeroIllustration(businessType) {
  return HERO[businessType] || null;
}
